import { type PropsWithChildren } from 'react';

import { DEFAULT_VALUE_ACCESSOR } from '../../../core/constants.js';
import { MeterBarChartDataContext } from '../context/MeterBarChartDataContext.js';
import { useMeterBarChartThresholds } from '../hooks/useMeterBarChartThresholds.js';
import { useMeterBarColorRule } from '../hooks/useMeterBarColorRule.js';
import { NAME_DEFAULT_VALUE, SEGMENT_COLOR_DEFAULT } from '../types/default.js';
import type { MeterBarChartDataProviderData } from '../types/meter-bar-chart-internal.js';
import type { MeterBarChartData } from '../types/meter-bar-chart.js';

interface MeterBarChartInternalDataProvidersProps {
  value: number | MeterBarChartData;
  displayValue: number;
  valueAccessor?: string;
  name?: string;
  color?: string;
}

export const MeterBarChartInternalDataProviders = ({
  value,
  displayValue,
  valueAccessor = DEFAULT_VALUE_ACCESSOR,
  name = NAME_DEFAULT_VALUE,
  color,
  children,
}: PropsWithChildren<MeterBarChartInternalDataProvidersProps>) => {
  const matchingColorRule = useMeterBarColorRule(value, valueAccessor);
  const { thresholdIndicators, matchingThreshold, legendItems } =
    useMeterBarChartThresholds(displayValue);

  const meterBarChartData: MeterBarChartDataProviderData = {
    color:
      matchingColorRule?.Default ??
      matchingThreshold?.color ??
      color ??
      SEGMENT_COLOR_DEFAULT,
    value,
    name,
    valueAccessor,
    thresholdIndicators,
    legendItems,
  };

  return (
    <MeterBarChartDataContext.Provider value={meterBarChartData}>
      {children}
    </MeterBarChartDataContext.Provider>
  );
};
